import {Injectable} from '@angular/core';

/** Session payload stored per tenant. */
export interface SessionRecord {
  token?: string;
  /** Base URL of the tenant API that issued the token. */
  issuer?: string;
  /** Access-token expiry (epoch ms) from JWT `exp` when present (AC-24). */
  expiresAt?: number | null;
  schemaVersion?: string | number | null;
  /** Persisted auth profile (includes token for backwards compatibility). */
  user?: Record<string, any>;
}

/**
 * Tenant-aware localStorage wrapper.
 *
 * - Session keys (currentUser, baseUrl, session) are namespaced by tenant id.
 * - Language/theme are stored as global keys (not tenant-scoped).
 * - Tenant id = stored domain, else hostname subdomain, else "localhost".
 */
@Injectable()
export class StorageService {

  private static readonly PREFIX = 'aysko';
  private static readonly GLOBAL_KEYS = new Set(['lang', 'theme', 'domain']);

  /** Resolve canonical tenant id for namespacing. */
  getTenantId(): string {
    const storedDomain = this.getRaw(this.globalKey('domain'));
    if (storedDomain) {
      return storedDomain;
    }
    return this.tenantIdFromHostname();
  }

  tenantIdFromHostname(): string {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (!hostname || hostname.indexOf('.') < 0) {
      return 'localhost';
    }
    const subdomain = hostname.split('.')[0];
    if (subdomain === 'localhost' || subdomain === 'example') {
      return 'localhost';
    }
    return subdomain;
  }

  getItem(key: string): string | null {
    return this.getRaw(this.resolveKey(key));
  }

  setItem(key: string, value: string): void {
    this.setRaw(this.resolveKey(key), value ?? '');
  }

  removeItem(key: string): void {
    this.removeRaw(this.resolveKey(key));
  }

  /** Safe JSON parse; corrupt / missing → defaultValue (or null). */
  getJson<T = any>(key: string, defaultValue: T | null = null): T | null {
    const raw = this.getItem(key);
    if (raw == null || raw === '') {
      return defaultValue;
    }
    try {
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  }

  setJson(key: string, value: any): void {
    this.setItem(key, JSON.stringify(value));
  }

  getSession(): SessionRecord | null {
    return this.getJson<SessionRecord>('session', null);
  }

  setSession(session: SessionRecord): void {
    this.setJson('session', session || {});
  }

  clearSession(): void {
    this.removeItem('session');
    this.removeItem('currentUser');
  }

  /** Clear tenant-scoped session keys (keeps global lang/theme/domain). */
  clearTenantSession(): void {
    this.clearSession();
    this.removeItem('baseUrl');
  }

  private resolveKey(key: string): string {
    if (StorageService.GLOBAL_KEYS.has(key)) {
      return this.globalKey(key);
    }
    return this.scopedKey(key);
  }

  private scopedKey(key: string): string {
    return `${StorageService.PREFIX}:${this.getTenantId()}:${key}`;
  }

  private globalKey(key: string): string {
    return `${StorageService.PREFIX}:global:${key}`;
  }

  private getRaw(fullKey: string): string | null {
    try {
      return localStorage.getItem(fullKey);
    } catch {
      return null;
    }
  }

  private setRaw(fullKey: string, value: string): void {
    try {
      localStorage.setItem(fullKey, value);
    } catch {
      // Quota / private mode — ignore
    }
  }

  private removeRaw(fullKey: string): void {
    try {
      localStorage.removeItem(fullKey);
    } catch {
      // ignore
    }
  }
}
